import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Languages, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { saveLanguage } from '../../utils/storage';
import AffiliateLogin from './AffiliateLogin.jsx';
import AffiliateRegister from './AffiliateRegister.jsx';
import Modal from '../Layout/Modal.jsx';

const AffiliateAuthContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [suspensionPopupMessage, setSuspensionPopupMessage] = useState('');
  const { toggleTheme, isDark } = useTheme();
  const { i18n } = useTranslation();
  
  const isLogin = location.pathname === '/affiliate/login';
  const isRegister = location.pathname === '/affiliate/register';

  useEffect(() => {
    if (!isLogin) return;
    const logoutMessage = localStorage.getItem('affiliateLogoutMessage');
    if (logoutMessage) {
      localStorage.removeItem('affiliateLogoutMessage');
      setSuspensionPopupMessage(logoutMessage);
    }
  }, [isLogin]);

  const handleToggle = (newPath) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    setTimeout(() => {
      navigate(newPath);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 50);
  };

  const toggleLanguage = () => {
    const newLanguage = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLanguage);
    saveLanguage(newLanguage);
  };

  const localizedSuspensionMessage = (() => {
    if (i18n.language !== 'ar') return suspensionPopupMessage;
    if (!suspensionPopupMessage) return '';
    const normalized = suspensionPopupMessage.toLowerCase();
    if (normalized.includes('suspended')) {
      return '.تم تعليق حساب التسويق بالعمولة الخاص بك. يرجى التواصل مع الدعم';
    }
    return suspensionPopupMessage;
  })();

  return (
    <>
      {/* Fixed Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="fixed bottom-6 right-6 z-50 glass-button p-3 rounded-full"
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      >
        {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>

      {/* Fixed Language Toggle Button */}
      <button
        onClick={toggleLanguage}
        className="fixed bottom-20 right-6 z-50 glass-button p-3 rounded-full"
        aria-label={`Switch to ${i18n.language === 'en' ? 'Arabic' : 'English'}`}
      >
        <span className="text-sm font-medium">
          {i18n.language === 'en' ? 'AR' : 'EN'}
        </span>
      </button>

      <div className="relative">
        {/* Login Form */}
        <div 
          className={`transition-all duration-500 ease-in-out transform ${
            isLogin 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-4 scale-95 pointer-events-none absolute inset-0'
          }`}
        >
          <AffiliateLogin onToggle={() => handleToggle('/affiliate/register')} />
        </div>

        {/* Register Form */}
        <div 
          className={`transition-all duration-500 ease-in-out transform ${
            isRegister 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-4 scale-95 pointer-events-none absolute inset-0'
          }`}
        >
          <AffiliateRegister onToggle={() => handleToggle('/affiliate/login')} />
        </div>
      </div>

      <Modal isOpen={!!suspensionPopupMessage} className="max-w-md mx-auto p-0" allowOutsideClick>
        <div className="glass-card">
          <div className="text-center py-2">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--error-color)' }} />
            <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
              {i18n.language === 'ar' ? 'تم تعليق الحساب' : 'Account Suspended'}
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              {localizedSuspensionMessage}
            </p>
            <button
              type="button"
              className="glass-button w-full py-3 font-semibold"
              onClick={() => setSuspensionPopupMessage('')}
            >
              {i18n.language === 'ar' ? 'حسنًا' : 'OK'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AffiliateAuthContainer;

